import React, { useState } from 'react';
import { FiX, FiDownload } from 'react-icons/fi';
import { Sheet } from '../../../../../components/Sheet/Sheet';
import * as translator from '../../../../../utils/translator';
import * as http from '../../../../../utils/http';
import { TreatmentDocumentsResponse, TreatmentDocument } from 'data-types';
import { localeByLanguage } from '../careProfileAssets';
import { MainTracker } from '../../../../../PixelTrackers/MainTracker';

interface TreatmentDocumentsSheetProps {
	isOpen: boolean;
	onClose: () => void;
	country: string;
	language: string;
	data: TreatmentDocumentsResponse | null;
	loading: boolean;
}

const downloadSuffixByType: Record<TreatmentDocument['type'], string> = {
	prescription: 'prescription/download',
	lab_result: 'test-results/download',
};

const saveBlob = (response: any, fileName: string) => {
	const blob = new Blob([response.data], { type: response.headers?.['content-type'] || 'application/pdf' });
	const url = window.URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.setAttribute('download', fileName);
	document.body.appendChild(link);
	link.click();
	link.parentNode?.removeChild(link);
	window.URL.revokeObjectURL(url);
};

const TreatmentDocumentsSheet: React.FC<TreatmentDocumentsSheetProps> = ({ isOpen, onClose, country, language, data, loading }) => {
	const [downloadingKey, setDownloadingKey] = useState<string | null>(null);
	const locale = localeByLanguage[language] || 'en-US';

	const handleDownload = (orderHashID: string, doc: TreatmentDocument) => {
		MainTracker.track('custom', 'View Consultation Record', { record_type: doc.type });
		const key = `${orderHashID}_${doc.type}`;
		const suffix = downloadSuffixByType[doc.type];
		const fileName = `${doc.type}_${orderHashID}.pdf`;

		setDownloadingKey(key);
		http
			.getFromServer(`${country}/account/orders/${orderHashID}/${suffix}`, [http.MIDDLEWARES.CUSTOMER_ID, http.MIDDLEWARES.GENERATE_FILE])
			.then((response: any) => saveBlob(response, fileName))
			.catch((error: any) => window.clientLogger.error('Failed to download treatment document:', error))
			.finally(() => setDownloadingKey(null));
	};

	const cycles = data?.cycles ?? [];
	const isEmpty = !loading && (!data?.enrolled || cycles.length === 0);

	return (
		<Sheet isOpen={isOpen} onClose={onClose}>
			<div id="treatment-documents-sheet" className="flex flex-col w-full">
				<div className="relative flex items-center justify-center px-8 py-4 flex-shrink-0">
					<p className="text-[20px] font-semibold text-care-plus-heading text-center leading-[1.2]">
						{translator.translate(language, 'my_account', 'CareProfile/Documents/Title')}
					</p>
					<button
						id="treatment-documents-close"
						type="button"
						onClick={onClose}
						aria-label="Close"
						className="absolute right-2 flex items-center justify-center w-8 h-8 rounded-full border-none bg-transparent text-care-plus-heading cursor-pointer"
					>
						<FiX size={19} />
					</button>
				</div>

				<div className="px-5 pb-8 overflow-y-auto">
					{loading && (
						<div className="flex flex-col items-center justify-center py-8 gap-y-4">
							<span className="w-9 h-9 rounded-full border-2 border-[#e7e4db] border-t-care-plus-orange animate-spin" />
						</div>
					)}

					{isEmpty && (
						<p className="text-center text-[15px] text-[#99a3b2] py-8">
							{translator.translate(language, 'my_account', 'CareProfile/Documents/Empty')}
						</p>
					)}

					{!loading && cycles.map((cycle) => {
						const dateLabel = new Date(cycle.date).toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
						return (
							<div key={cycle.orderHashID} className="flex flex-col gap-y-3 mb-6">
								<p className="text-sm font-semibold text-care-plus-heading">
									{translator.translate(language, 'my_account', 'CareProfile/Documents/Cycle').replace('{{index}}', String(cycle.cycleIndex))} · {dateLabel}
								</p>

								{cycle.documents.length === 0 && (
									<p className="text-[13px] text-[#99a3b2]">
										{translator.translate(language, 'my_account', 'CareProfile/Documents/EmptyCycle')}
									</p>
								)}

								{cycle.documents.map((doc) => {
									const key = `${cycle.orderHashID}_${doc.type}`;
									const isDownloading = downloadingKey === key;
									const docDateLabel = new Date(doc.date).toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
									return (
										<div
											key={key}
											className="flex items-center justify-between gap-x-3 bg-white border border-solid border-[#e7e4db] rounded-[14px] px-4 py-3"
										>
											<div className="flex flex-col min-w-0">
												<span className="text-[15px] font-medium text-care-plus-heading truncate">
													{translator.translate(language, 'my_account', doc.labelKey)}
												</span>
												<span className="text-[13px] text-[#99a3b2]">{docDateLabel}</span>
											</div>
											<button
												type="button"
												disabled={isDownloading}
												onClick={() => handleDownload(cycle.orderHashID, doc)}
												aria-label={translator.translate(language, 'my_account', 'CareProfile/Documents/Download')}
												className="flex items-center justify-center w-9 h-9 rounded-full border-none bg-[#faf4ef] text-care-plus-orange cursor-pointer disabled:opacity-50 flex-shrink-0"
											>
												{isDownloading ? (
													<span className="w-4 h-4 rounded-full border-2 border-[#e7e4db] border-t-care-plus-orange animate-spin" />
												) : (
													<FiDownload size={16} />
												)}
											</button>
										</div>
									);
								})}
							</div>
						);
					})}
				</div>
			</div>
		</Sheet>
	);
};

export default TreatmentDocumentsSheet;
